import { Button } from '@design-system';
import { formatPrice } from '@utils/prices';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import { Image } from '../../../../components/Image';
import { pathFor } from '../../../../helpers/links';
import { GeneratedMoneySet } from '../../../../types/generated';
import { CurrencyCode } from '../../../../types/Locations';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';

interface DrawEntryConfirmationDialogProps {
  backgroundImage: StoryblokImage | null;
  image?: StoryblokImage;
  onClose: () => void;
  isAmlRequired: boolean;
  isPreOrder: boolean;
  productTitle: string;
  bid?: GeneratedMoneySet | null;
  isPreOrderOptIn: boolean;
}

const copiesByDrawType = {
  preOrder: {
    aml: {
      buttonText: 'drawers.review.draw.preOrderConfirmationDialog.amlButton',
      paragraph: 'drawers.review.draw.preOrderConfirmationDialog.bodyAml',
      title: 'drawers.review.draw.preOrderConfirmationDialog.title',
    },
    buttonText: 'drawers.review.draw.preOrderConfirmationDialog.discoverButton',
    title: 'drawers.review.draw.preOrderConfirmationDialog.title',
    paragraph: 'drawers.review.draw.preOrderConfirmationDialog.body',
  },
  auction: {
    aml: {
      buttonText: 'drawers.review.draw.auctionConfirmationDialog.amlButton',
      paragraph: 'drawers.review.draw.auctionConfirmationDialog.bodyAml',
      title: 'drawers.review.draw.auctionConfirmationDialog.title',
    },
    buttonText: 'drawers.review.draw.auctionConfirmationDialog.discoverButton',
    title: 'drawers.review.draw.auctionConfirmationDialog.title',
    paragraph: 'drawers.review.draw.auctionConfirmationDialog.body',
  },
  draw: {
    aml: {
      buttonText: 'drawers.review.draw.confirmationDialog.amlButton',
      paragraph: 'drawers.review.draw.confirmationDialog.bodyAml',
      title: 'drawers.review.draw.confirmationDialog.title',
    },
    buttonText: 'drawers.review.draw.confirmationDialog.discoverButton',
    title: 'drawers.review.draw.confirmationDialog.title',
    paragraph: 'drawers.review.draw.confirmationDialog.body',
  },
  drawPreOrderOptIn: {
    title: 'drawers.review.draw.preOrderOptInConfirmationDialog.title',
    paragraph:
      'drawers.review.draw.preOrderOptInConfirmationDialog.description',
    buttonText: 'drawers.review.draw.confirmationDialog.discoverButton',
    aml: {
      buttonText: 'drawers.review.draw.confirmationDialog.amlButton',
      paragraph: 'drawers.review.draw.confirmationDialog.bodyAml',
      title: 'drawers.review.draw.confirmationDialog.title',
    },
  },
};

export const DrawEntryConfirmationDialog: React.FC<
  DrawEntryConfirmationDialogProps
> = ({
  backgroundImage,
  bid,
  image,
  isAmlRequired,
  isPreOrder,
  isPreOrderOptIn,
  onClose,
  productTitle,
}) => {
  const router = useRouter();
  const { t } = useTranslation(['product']);

  const copies = useMemo(() => {
    let selectedCopies = copiesByDrawType.draw;
    if (isPreOrder) {
      selectedCopies = copiesByDrawType.preOrder;
    }
    if (bid) {
      selectedCopies = copiesByDrawType.auction;
    }
    if (isPreOrderOptIn) {
      selectedCopies = copiesByDrawType.drawPreOrderOptIn;
    }
    return isAmlRequired ? selectedCopies.aml : selectedCopies;
  }, [isPreOrder, bid, isPreOrderOptIn, isAmlRequired]);

  const [emitter] = useAnalyticsEmitter();

  const handleClose = () => {
    emitter('close', 'clicked');
    onClose();
  };

  return (
    <div className="relative flex w-full max-w-md flex-col overflow-hidden rounded-t-lg bg-white md:rounded-lg">
      <div className="absolute right-6 top-6 flex items-center justify-center">
        <Button
          hideLabel
          icon="ic_close"
          label="Close"
          onClick={handleClose}
          size="sm"
          variant="secondary"
        />
      </div>

      <div className="relative flex aspect-[3/2] w-full flex-col items-center justify-center">
        {backgroundImage ? (
          <Image
            alt={backgroundImage?.alt ?? ''}
            size={{
              sm: { span: 3, ratio: '3/2' },
              md: { span: 4, ratio: '3/2' },
              lg: { span: 12, ratio: '3/2' },
            }}
            src={backgroundImage.filename}
          />
        ) : null}

        {image ? (
          <div className="absolute z-10 aspect-[1/1] w-24 overflow-hidden rounded-sm shadow-lg">
            <Image alt={image?.alt ?? ''} src={image.filename} width={96} />
          </div>
        ) : null}
      </div>

      <div className="p-6">
        <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
          <p className="text-md font-bold">{t(copies.title)}</p>
          <p className="mb-6 mt-3 text-neutral-4">
            {t(copies.paragraph, {
              productTitle,
              bid: bid?.amount
                ? formatPrice(
                    Number(bid.amount),
                    bid.currencyCode as CurrencyCode,
                  )
                : undefined,
            })}
          </p>
          <Button
            fullWidth
            label={t(copies.buttonText)}
            onClick={() => {
              if (isAmlRequired) {
                emitter('AML', 'clicked');
                router.push('/aml');
              } else {
                emitter('discover', 'clicked');
                router.push(pathFor({ page: 'WORKS_INDEX' }));
              }
              handleClose();
            }}
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
};
